import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { RouterSegments } from 'src/app/models/router-segments';
import { InstancesService } from 'src/app/services/api/instances.service';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { SystemService } from 'src/app/services/api/system.service';
import { TenantService } from 'src/app/services/api/tenant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { UrlService } from 'src/app/services/util/url.service';
import { ClipboardService } from 'ngx-clipboard';
import { MatTableDataSource } from '@angular/material/table';
import { HttpClient } from '@angular/common/http';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';
import { Attributes, Datazone, Permissions } from 'src/app/models/principal/permission';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { MatDialog } from '@angular/material/dialog';

const REQUIRED_PERMISSIONS = [
  {
    key: PermissionKeys.TENANT_READ,
    action: Permissions.TENANT_READ
  },
  {
    key: PermissionKeys.TENANT_ONBOARDING_DATAZONE_NONPII,
    action: Permissions.TENANT_ONBOARDING,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.NONPII
  },
  {
    key: PermissionKeys.TENANT_ONBOARDING_DATAZONE_PII,
    action: Permissions.TENANT_ONBOARDING,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.PII
  },
  {
    key: PermissionKeys.TENANT_ONBOARDING_DATAZONE_LIVE,
    action: Permissions.TENANT_ONBOARDING,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.LIVE
  }
];

@Component({
    selector: 'app-shared-service-details',
    templateUrl: './shared-service-details.component.html',
    styleUrls: ['./shared-service-details.component.scss'],
    standalone: false
})
export class SharedServiceDetailsComponent  implements OnInit {
  private destroyRef = inject(DestroyRef);

  PermissionKeys = PermissionKeys;
  permissionsMap: { [key: string]: boolean };
  breadcrumbItems: BreadcrumbItem[] = [];
  tenantId: string;
  RouterSegments = RouterSegments;
  tenantDetails: any;
  solutionId: string;
  currentVersion: any;
  solutionVersions: any;
  solution: any;
  copyGitLab = false;
  copyKeycloak = false;
  DeployStatus = DeployStatus;
  isLoading = true;
  currentFile: File[];
  uploadTableData: any = [];
  instanceBySerDefColumns = ['fileName', 'version', 'time', 'status'];
  certificateData: any;
  formatError = false;
  uploadDisable = false;
  readonly inprogressStatus = [DeployStatus.DEPLOYING, DeployStatus.TERMINATING, DeployStatus.CONFIGURING, DeployStatus.UPDATING, DeployStatus.TESTING,
    DeployStatus.FAILOVER_APPLYING, DeployStatus.FAILBACK_APPLYING, DeployStatus.PRE_FAILBACK_APPLYING, DeployStatus.POST_FAILBACK_APPLYING, DeployStatus.OPERATION_INPROGRESS];
  
  nonPiiDownload: boolean;
  nonProdDownload: boolean;
  piiDownload: boolean;
  prodDownload: boolean;
  dataZone: any;

  constructor(private route: ActivatedRoute, public authService: AuthService, private toastrService: CustomToastrService,
    private tenantService: TenantService, private instanceService: InstancesService, private systemService: SystemService,
    private solutionsService: SolutionsService, public urlService: UrlService, private clipboardApi: ClipboardService,
    private sidenavService: SidenavService, private router: Router, private permissionsService: PermissionsService, private dialog: MatDialog
  ) {
    
  }

  ngOnInit(): void {
    this.sidenavService.tenantSelectedMenu = 'LEFT_SIDE_NAV.SHARED_SERVICES';
    this.dataZone = this.route.snapshot.params.envt;
    const tenant = this.authService.principal.provider;
    this.permissionsMap = this.permissionsService.resolveAPIPermissions(REQUIRED_PERMISSIONS);
    if (!this.permissionsMap[PermissionKeys.TENANT_READ]) {
      this.router.navigate(['/', tenant, RouterSegments.TENANT, 'permission-unauthorized'], {skipLocationChange: true});
    }
    this.tenantId = this.route.snapshot.params.id;
    this.breadcrumbItems.push({icon: 'assets/images/home_icon.svg', url: [RouterSegments.TENANT, this.tenantId] },
      {label: translate('TENANT_DETAILS.SHARED_SERVICES'), url:[RouterSegments.TENANT, this.tenantId, RouterSegments.SHARED_SERVICES]},
      {label: this.dataZone === 'pii' ? translate('TENANT_LIST.PII') : (this.dataZone === 'prod' ? translate('TENANT_LIST.PROD') : (this.dataZone === 'nonpii' ? translate('TENANT_LIST.NON_PII') : translate('TENANT_LIST.NON_PROD')))});
    this.getTenantData();
  }

  getTenantData(): void {
    this.tenantService.getTenantDetails(this.tenantId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(data => {
      this.tenantDetails = data;
      if(this.dataZone === 'nonpii') {
        if (this.tenantDetails?.sharedServices?.nonpii?.messaging?.clientCert?.length > 0) {
          this.certificateData = this.tenantDetails?.sharedServices?.nonpii?.messaging?.clientCert.sort((a, b) => new Date(a?.time)?.getTime() - new Date(b?.time)?.getTime() ? -1 : 1);
          this.certificateData.map(item => item.isActive = false);
          if (this.certificateData?.find(item => item.status === 'added')) {
            this.nonPiiDownload = true;
            this.certificateData.find(item => item.status === 'added').isActive = true;
          }
          if (this.certificateData?.find(item => item.status === 'adding')) {
            this.uploadDisable = true;
          }
          this.uploadTableData = new MatTableDataSource(this.certificateData);
        }
      }else if(this.dataZone === 'nonprod') {
        if (this.tenantDetails?.sharedServices?.nonprod?.messaging?.clientCert?.length > 0) {
          this.certificateData = this.tenantDetails?.sharedServices?.nonprod?.messaging?.clientCert.sort((a, b) => new Date(a?.time)?.getTime() - new Date(b?.time)?.getTime() ? -1 : 1);
          this.certificateData.map(item => item.isActive = false);
          if (this.certificateData?.find(item => item.status === 'added')) {
            this.nonProdDownload = true;
            this.certificateData.find(item => item.status === 'added').isActive = true;
          }
          if (this.certificateData?.find(item => item.status === 'adding')) {
            this.uploadDisable = true;
          }
          this.uploadTableData = new MatTableDataSource(this.certificateData);
        }
      } else if(this.dataZone === 'pii') {
      if (this.tenantDetails?.sharedServices?.pii?.messaging?.clientCert?.length > 0) {
        this.certificateData = this.tenantDetails?.sharedServices?.pii?.messaging?.clientCert.sort((a, b) => new Date(a?.time)?.getTime() - new Date(b?.time)?.getTime() ? -1 : 1);
        this.certificateData.map(item => item.isActive = false);
        if (this.certificateData?.find(item => item.status === 'added')) {
          this.piiDownload = true;
          this.certificateData.find(item => item.status === 'added').isActive = true;
        }
        if (this.certificateData?.find(item => item.status === 'adding')) {
          this.uploadDisable = true;
        }
        this.uploadTableData = new MatTableDataSource(this.certificateData);
      }
    } else if(this.dataZone === 'prod') {
      if (this.tenantDetails?.sharedServices?.prod?.messaging?.clientCert?.length > 0) {
        this.certificateData = this.tenantDetails?.sharedServices?.prod?.messaging?.clientCert.sort((a, b) => new Date(a?.time)?.getTime() - new Date(b?.time)?.getTime() ? -1 : 1);
        this.certificateData.map(item => item.isActive = false);
        if (this.certificateData?.find(item => item.status === 'added')) {
          this.prodDownload = true;
          this.certificateData.find(item => item.status === 'added').isActive = true;
        }
        if (this.certificateData?.find(item => item.status === 'adding')) {
          this.uploadDisable = true;
        }
        this.uploadTableData = new MatTableDataSource(this.certificateData);
      }
    }
      this.getSolutionVersions();
    },
    (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }

  getSolutionVersions(): void {
    this.systemService.getSystem()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(response => {
      if (response) {
        if (this.tenantDetails?.cloudProvider === 'AWS' || this.tenantDetails?.targetCloud === 'AWS') {
          this.solutionId = response.awsTssSolutionId;
        } else if(this.tenantDetails?.cloudProvider === 'Azure' || this.tenantDetails?.targetCloud === 'Azure'){
          this.solutionId = response.azureTssSolutionId;
        }
      }
      this.getSolutionDetails();
      if (this.solutionId) {
        this.solutionsService.getSolutionVersions(this.solutionId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(data => {
          if (data) {
            this.solutionVersions = data;
            this.getInstanceData();
          }
        });
      }
    },
    (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }

  getSolutionDetails(): void {
    this.solutionsService.getSolutionById(this.solutionId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(result => {
      this.solution = result;
    },
    (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }

  getInstanceData(): void {
    if(this.dataZone === 'nonpii') {
      this.instanceService.getInstanceById(this.tenantDetails?.sharedServices?.nonpii?.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(instanceData => {
        this.tenantDetails.sharedServices.nonpii.instanceDetails = instanceData;
        this.currentVersion = this.solutionVersions?.find(item => item.id === instanceData.solutionVersionId);
        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
    } else if(this.dataZone === 'nonprod') {
      this.instanceService.getInstanceById(this.tenantDetails?.sharedServices?.nonprod?.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(instanceData => {
        this.tenantDetails.sharedServices.nonprod.instanceDetails = instanceData;
        this.currentVersion = this.solutionVersions?.find(item => item.id === instanceData.solutionVersionId);
        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
    } else if(this.dataZone === 'pii') {
    this.instanceService.getInstanceById(this.tenantDetails?.sharedServices?.pii?.instanceId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(instanceData => {
      this.tenantDetails.sharedServices.pii.instanceDetails = instanceData;
      this.currentVersion = this.solutionVersions?.find(item => item.id === instanceData.solutionVersionId);
      this.isLoading = false;
    },
    (error) => {
      this.isLoading = false;
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
    } else if(this.dataZone === 'prod') {
      this.instanceService.getInstanceById(this.tenantDetails?.sharedServices?.prod?.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(instanceData => {
        this.tenantDetails.sharedServices.prod.instanceDetails = instanceData;
        this.currentVersion = this.solutionVersions?.find(item => item.id === instanceData.solutionVersionId);
        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
    }
  }

  onCopyId(data, icon): void {
    icon === 'gitlab' ? this.copyGitLab = true : icon === 'keycloak' ? this.copyKeycloak = true : '';
    this.clipboardApi.copyFromContent(data);
    setTimeout(() => {
       icon === 'gitlab' ? this.copyGitLab = false : icon === 'keycloak' ? this.copyKeycloak = false : '';
    }, 500);
  }

  selectFile(): void {
    let element = document.getElementById('upload_file') as HTMLInputElement;
    element.click();
  }

  onChange(event) {
    this.formatError = false;
    this.currentFile = event.target.files;
    if (this.currentFile[0]?.name?.split('.')?.pop() !== 'crt') {
      this.formatError = true;
    }
  }

  removeFile(): void {
    this.currentFile = undefined;
  }

  uploadFile(): void {
    let text;
    var reader = new FileReader();
    reader.readAsText(this.currentFile[0]);
    reader.onload = (evt) => {
      text = evt.target.result;
      this.putCertificate(btoa(text));
    }
  }

  putCertificate(encodedText) {
    const dataZone = this.route.snapshot.params.envt;
    this.uploadDisable = true;
    this.tenantService.putMessagingClientCert(this.tenantId, encodedText, this.currentFile[0].name, dataZone)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(response => {
      if (response) {
        this.currentFile = undefined;
        let sortData;
        if(dataZone === 'nonpii') {
          sortData = response.sharedServices?.nonpii?.messaging?.clientCert;
        } else if(dataZone === 'nonprod') {
          sortData = response.sharedServices?.nonprod?.messaging?.clientCert;
        } else if(dataZone === 'pii') {
        sortData = response.sharedServices?.pii?.messaging?.clientCert;
        } else if(dataZone === 'prod') {
        sortData = response.sharedServices?.prod?.messaging?.clientCert;
        }
        this.certificateData = sortData.sort((a, b) => new Date(a?.time)?.getTime() - new Date(b?.time)?.getTime() ? -1 : 1);
        this.certificateData.map(item => item.isActive = false);
        if (this.certificateData?.find(item => item.status === 'added')) {
          this.certificateData.find(item => item.status === 'added').isActive = true;
        }
        if (this.certificateData?.find(item => item.status === 'adding')) {
          this.uploadDisable = true;
        }
        this.uploadTableData = new MatTableDataSource(this.certificateData);
            setTimeout(() => {
              const title = translate('LEFT_SIDE_NAV.CERTIFICATE');
              const message = translate('INSTANCE_DETAILS.CERTIFICATE_SUCCESS_MSG');
              this.toastrService.successMessage(message, title);
            }, 500);
      }
    },
    (error) => {
      this.uploadDisable = false;
      const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
      this.dialog.open(ErrorDialogComponent, {
        minHeight: '342px',
        width: '630px',
        panelClass: 'flex-dialog',
        data: {title: translate('ERROR_DIALOG.UPDATE_CERTIFICATE_FAILED'),
          description: errorMessage
        }
      })
    });
  }

  downloadFile(): void {
    const datazone = this.route.snapshot.params.envt;
    this.tenantService.getMessagingClientCert(this.tenantId, datazone).pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(response => {
      const linkSource = 'data:application/octet-stream;base64,' + response?.serverCertData;
      const downloadLink = document.createElement('a');
      const fileName = response?.fileName;
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    },
    (error) => {
        setTimeout(() => {
		    const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }
}
